// Shared vocabulary for the API tokens UI. Mirrors ALLOWED_SCOPES /
// DEFAULT_SCOPES in core/src/auth/api_tokens.py - keep the two in sync.
//
// Scopes follow `<resource>.<action>` and cover the same resource areas as
// the product navigation: Workspaces, Goals, Data (sources + signals),
// Intelligence (discovery), Modeling (segments / models / trainings) and
// Serving (deployments / inferences).

export const AVAILABLE_SCOPES = [
  // Workspaces
  {
    value: 'workspaces.read',
    label: 'Read workspaces',
    description: 'List workspaces and view their settings',
    group: 'Workspaces'
  },
  {
    value: 'workspaces.write',
    label: 'Write workspaces',
    description: 'Create, update and delete workspaces',
    group: 'Workspaces'
  },

  // Goals
  {
    value: 'goals.read',
    label: 'Read goals',
    description: 'View goals, their drivers and progress',
    group: 'Goals'
  },
  {
    value: 'goals.write',
    label: 'Write goals',
    description: 'Create, update and delete goals',
    group: 'Goals'
  },

  // Data (sources, ingestion, signal universe)
  {
    value: 'data.read',
    label: 'Read data',
    description: 'View data sources, ingestion jobs and raw data',
    group: 'Data'
  },
  {
    value: 'data.write',
    label: 'Write data',
    description: 'Connect data sources, upload data and run ingestion',
    group: 'Data'
  },
  {
    value: 'signals.read',
    label: 'Read signals',
    description: 'View data signals, events and entities',
    group: 'Data'
  },
  {
    value: 'signals.write',
    label: 'Write signals',
    description: 'Create and modify data signals',
    group: 'Data'
  },

  // Intelligence
  {
    value: 'discovery.read',
    label: 'Read discoveries',
    description: 'View discovery insights and relationships',
    group: 'Intelligence'
  },
  {
    value: 'discovery.write',
    label: 'Run discoveries',
    description: 'Trigger and manage discovery runs',
    group: 'Intelligence'
  },

  // Modeling
  {
    value: 'segments.read',
    label: 'Read segments',
    description: 'View segments and their feature grids',
    group: 'Modeling'
  },
  {
    value: 'segments.write',
    label: 'Write segments',
    description: 'Create, update and delete segments',
    group: 'Modeling'
  },
  {
    value: 'models.read',
    label: 'Read models',
    description: 'View models and pipelines',
    group: 'Modeling'
  },
  {
    value: 'models.write',
    label: 'Write models',
    description: 'Create, update and delete models and pipelines',
    group: 'Modeling'
  },
  {
    value: 'trainings.read',
    label: 'Read trainings',
    description: 'View training runs, metrics and artifacts',
    group: 'Modeling'
  },
  {
    value: 'trainings.run',
    label: 'Run trainings',
    description: 'Start, stop and manage training runs',
    group: 'Modeling'
  },

  // Serving
  {
    value: 'deployments.read',
    label: 'Read deployments',
    description: 'View deployments and their status',
    group: 'Serving'
  },
  {
    value: 'deployments.write',
    label: 'Write deployments',
    description: 'Deploy, promote and retire model versions',
    group: 'Serving'
  },
  {
    value: 'inferences.read',
    label: 'Read inferences',
    description: 'View inference results and history',
    group: 'Serving'
  },
  {
    value: 'inferences.create',
    label: 'Create inferences',
    description: 'Request predictions from deployed models',
    group: 'Serving'
  }
];

// Order in which groups render in the scope picker.
export const SCOPE_GROUPS = ['Workspaces', 'Goals', 'Data', 'Intelligence', 'Modeling', 'Serving'];

// Older tokens may carry these; the backend still accepts them but the
// picker shows the current vocabulary. Kept so labels render nicely.
export const LEGACY_SCOPE_LABELS = {
  'models.deploy': 'Deploy models (legacy)'
};

export const DEFAULT_SCOPES = ['workspaces.read', 'goals.read', 'models.read', 'inferences.create'];

// value in days; null means the token never expires
export const EXPIRY_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 365, label: '1 year' },
  { value: null, label: 'Never expires' }
];

export const MAX_TOKEN_NAME_LENGTH = 100;

export const scopeLabel = (scope) =>
  AVAILABLE_SCOPES.find((s) => s.value === scope)?.label
    || LEGACY_SCOPE_LABELS[scope]
    || scope;
